import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { FiTrash2, FiPlus, FiRefreshCw, FiImage } from "react-icons/fi";
import {
  getGRNById,
  updateGRN,
  getGRNItems,
  createGRNItem,
  deleteGRNItem,
} from "../../Services/GRNService";
import {
  getPurchaseOrderItems,
  refreshPurchaseOrder,
} from "../../Services/PurchaseOrderService";
import {
  Loader,
  Button,
  Modal,
  ConfirmDeleteModal,
  ExpandableInfoPanel,
} from "../../Components/Common";
import {
  MESSAGES,
  GRN_INFO_LABELS,
  APPROVAL_STATUS_STYLES,
  ITEM_STATUS_STYLES,
  ITEM_STATUS_LABELS,
  CONFIRM_DELETE_GRN_ITEM,
  ACCEPTED,
  APPROVED,
} from "./constants";
import { GRN_LIST } from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";

const ITEMS_PAGINATION = { count: 0, offset: 0, limit: 5 };

const formatLabel = (value) =>
  (value || "-")
    .toString()
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatDateLong = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const emptyAddForm = () => ({
  received_amount: 0,
  item_status: ACCEPTED,
  photoFile: null,
  photoPreview: null,
});

const GrnDetails = () => {
  const { grnId } = useParams();
  const navigate = useNavigate();

  const [grn, setGrn] = useState(null);
  const [loadingGrn, setLoadingGrn] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [approving, setApproving] = useState(false);

  const [poItems, setPoItems] = useState([]);
  const [poItemsPagination, setPoItemsPagination] = useState(ITEMS_PAGINATION);
  const [poItemsLoading, setPoItemsLoading] = useState(false);
  const [poItemsPaginationLoading, setPoItemsPaginationLoading] = useState(false);

  const [receivedItems, setReceivedItems] = useState([]);
  const [itemsPagination, setItemsPagination] = useState(ITEMS_PAGINATION);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPaginationLoading, setItemsPaginationLoading] = useState(false);

  const [addModalItem, setAddModalItem] = useState(null);
  const [addForm, setAddForm] = useState(emptyAddForm());
  const [adding, setAdding] = useState(false);

  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [mismatchItems, setMismatchItems] = useState([]);
  const [loadingMismatch, setLoadingMismatch] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadGrn = useCallback(async () => {
    setLoadingGrn(true);
    try {
      const response = await getGRNById(grnId);
      if (response.status === 200) {
        setGrn(response.data);
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.DETAIL_LOAD_ERROR);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.DETAIL_LOAD_ERROR);
    } finally {
      setLoadingGrn(false);
    }
  }, [grnId]);

  const loadPoItems = async (finalParams, isInitial = false) => {
    if (!grn?.purchase_order) return;
    if (isInitial) {
      setPoItemsLoading(true);
    } else {
      setPoItemsPaginationLoading(true);
    }

    try {
      const response = await getPurchaseOrderItems(grn.purchase_order, finalParams);
      if (response.status === 200) {
        const newItems = response.data.results || [];
        setPoItems((prev) => (isInitial ? newItems : [...prev, ...newItems]));
        setPoItemsPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.DETAIL_LOAD_ERROR);
        if (isInitial) setPoItems([]);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.DETAIL_LOAD_ERROR);
      if (isInitial) setPoItems([]);
    } finally {
      if (isInitial) {
        setPoItemsLoading(false);
      } else {
        setPoItemsPaginationLoading(false);
      }
    }
  };

  const loadMorePoItems = () => {
    if (poItemsPagination.offset >= poItemsPagination.count) return;
    loadPoItems({ offset: poItemsPagination.offset, limit: poItemsPagination.limit });
  };

  const refreshPoItemsList = () => {
    setPoItemsPagination(ITEMS_PAGINATION);
    loadPoItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
  };

  const loadItems = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setItemsLoading(true);
    } else {
      setItemsPaginationLoading(true);
    }

    try {
      const response = await getGRNItems(grnId, finalParams);
      if (response.status === 200) {
        const newItems = response.data.results || [];
        setReceivedItems((prev) => (isInitial ? newItems : [...prev, ...newItems]));
        setItemsPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.ITEMS_LOAD_ERROR);
        if (isInitial) setReceivedItems([]);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.ITEMS_LOAD_ERROR);
      if (isInitial) setReceivedItems([]);
    } finally {
      if (isInitial) {
        setItemsLoading(false);
      } else {
        setItemsPaginationLoading(false);
      }
    }
  };

  const loadMoreItems = () => {
    if (itemsPagination.offset >= itemsPagination.count) return;
    loadItems({ offset: itemsPagination.offset, limit: itemsPagination.limit });
  };

  const refreshItemsList = () => {
    setItemsPagination(ITEMS_PAGINATION);
    loadItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
  };

  // react-infinite-scroll-component only loads more on a scroll event; if a
  // page of results doesn't fill (overflow) the scrollable box, there's
  // nothing to scroll and it can never trigger the next page on its own.
  useEffect(() => {
    if (poItemsPaginationLoading || poItemsLoading) return;
    const el = document.getElementById("scrollablePoItemsDiv");
    if (
      el &&
      el.scrollHeight <= el.clientHeight &&
      poItemsPagination.offset < poItemsPagination.count
    ) {
      loadMorePoItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poItems, poItemsPagination, poItemsPaginationLoading, poItemsLoading]);

  useEffect(() => {
    if (itemsPaginationLoading || itemsLoading) return;
    const el = document.getElementById("scrollableGrnItemsDiv");
    if (
      el &&
      el.scrollHeight <= el.clientHeight &&
      itemsPagination.offset < itemsPagination.count
    ) {
      loadMoreItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedItems, itemsPagination, itemsPaginationLoading, itemsLoading]);

  useEffect(() => {
    loadGrn();
    loadItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnId]);

  useEffect(() => {
    if (grn?.purchase_order) {
      loadPoItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grn?.purchase_order]);

  const handleRefreshPoItems = async () => {
    if (!grn?.purchase_order) return;
    setRefreshing(true);
    try {
      const response = await refreshPurchaseOrder(grn.purchase_order);
      if (response.status === 200) {
        refreshPoItemsList();
        toast.success(MESSAGES.REFRESH_PO_ITEMS_SUCCESS);
      } else {
        toast.error(
          extractErrorMessage(response) || MESSAGES.REFRESH_PO_ITEMS_ERROR
        );
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || MESSAGES.REFRESH_PO_ITEMS_ERROR
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      // Approving always implies the GRN is fully received - sending both in
      // one request means the "ready to bill" notification to Accounts can
      // never be missed by a separately forgotten checkbox click.
      const response = await updateGRN(grnId, {
        approval_status: APPROVED,
        all_items_received: true,
      });
      if (response.status === 200) {
        setGrn((prev) => ({ ...prev, ...response.data }));
        toast.success(MESSAGES.UPDATE_SUCCESS);
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.UPDATE_ERROR);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.UPDATE_ERROR);
    } finally {
      setApproving(false);
    }
  };

  const openApproveConfirm = async () => {
    setApproveConfirmOpen(true);
    setLoadingMismatch(true);
    try {
      // poItems is paginated (only what's been scrolled into view), so pull
      // the full item list here to check for mismatches reliably.
      const response = await getPurchaseOrderItems(grn.purchase_order, {
        offset: 0,
        limit: 1000,
      });
      if (response.status === 200) {
        const allItems = response.data.results || [];
        setMismatchItems(
          allItems.filter(
            (item) => !item.is_deleted && Number(item.remaining_quantity) > 0
          )
        );
      }
    } catch (error) {
      // Non-fatal: the approve confirmation still works without the mismatch list.
    } finally {
      setLoadingMismatch(false);
    }
  };

  const closeApproveConfirm = () => {
    setApproveConfirmOpen(false);
    setMismatchItems([]);
  };

  const confirmApprove = async () => {
    await handleApprove();
    closeApproveConfirm();
  };

  const openAddModal = (poItem) => {
    setAddModalItem(poItem);
    setAddForm(emptyAddForm());
  };

  const closeAddModal = () => {
    setAddModalItem(null);
    setAdding(false);
  };

  const handleAddFieldChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPhotoChange = (file) => {
    setAddForm((prev) => ({
      ...prev,
      photoFile: file,
      photoPreview: URL.createObjectURL(file),
    }));
  };

  const handleSubmitAdd = async () => {
    if (!addModalItem) return;

    const receivedQty = Number(addForm.received_amount);
    if (!receivedQty || receivedQty <= 0) {
      toast.error("Enter a valid received quantity");
      return;
    }

    const remainingQty = Number(addModalItem.remaining_quantity);
    if (receivedQty > remainingQty) {
      toast.error(
        `Cannot receive more than the remaining quantity (${remainingQty} ${addModalItem.unit})`
      );
      return;
    }

    setAdding(true);
    try {
      const formData = new FormData();
      formData.append("po_item", addModalItem.id);
      formData.append("received_amount", addForm.received_amount);
      formData.append("item_status", addForm.item_status);
      if (addForm.photoFile instanceof File) {
        formData.append("photo", addForm.photoFile);
      }

      const response = await createGRNItem(grnId, formData);
      if (response.status === 201 || response.status === 200) {
        toast.success(MESSAGES.ITEM_ADD_SUCCESS);
        closeAddModal();
        refreshItemsList();
        refreshPoItemsList();
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.ITEM_ADD_ERROR);
        setAdding(false);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.ITEM_ADD_ERROR);
      setAdding(false);
    }
  };

  const openDeleteConfirm = (row) => {
    setDeleteTarget(row);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteGRNItem(grnId, deleteTarget.id);
      if (response.status === 200 || response.status === 204) {
        toast.success(MESSAGES.ITEM_REMOVE_SUCCESS);
        refreshItemsList();
        refreshPoItemsList();
        closeDeleteConfirm();
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.ITEM_REMOVE_ERROR);
        setDeleting(false);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.ITEM_REMOVE_ERROR);
      setDeleting(false);
    }
  };

  if (loadingGrn || !grn) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Loader size={4} />
      </div>
    );
  }

  const statusStyle =
    APPROVAL_STATUS_STYLES[grn.approval_status] || "bg-gray-100 text-gray-700";
  const canApprove = grn.approval_status !== APPROVED;

  return (
    <div className="px-4 my-4">
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate(GRN_LIST)}
          className="text-primary text-2xl"
        >
          <HiOutlineArrowLeft />
        </button>
        <div className="text-xl md:text-2xl text-primary font-semibold flex-1 truncate">
          {grn.grn_id}
        </div>
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusStyle}`}
        >
          {formatLabel(grn.approval_status)}
        </span>
      </div>

      {/* GRN + PO Info */}
      <ExpandableInfoPanel
        title={grn.vendor_name}
        subtitle={`${grn.site_name || "-"} · PO ${formatDateLong(grn.po_date)}`}
        fields={[
          { label: GRN_INFO_LABELS.zoho_po_number, value: grn.zoho_po_number },
          { label: GRN_INFO_LABELS.vendor_name, value: grn.vendor_name },
          { label: GRN_INFO_LABELS.site_name, value: grn.site_name },
          { label: GRN_INFO_LABELS.po_date, value: formatDateDDMMYYYY(grn.po_date) },
          { label: GRN_INFO_LABELS.created_by, value: grn.created_by },
          {
            label: GRN_INFO_LABELS.created,
            value: grn.created
              ? `${new Date(grn.created).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })} IST`
              : "-",
          },
        ]}
        mobileFields={[
          { label: "Vendor", value: grn.vendor_name },
          { label: "Site", value: grn.site_name },
        ]}
        footer={`GRN raised by ${grn.created_by || "-"} on ${formatDateLong(
          grn.created
        )}`}
      />

      {canApprove && (
        <div className="border rounded-lg mb-4 bg-gray-50">
          {/* Desktop: heading + description + button */}
          <div className="hidden md:block p-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-base leading-none">✨</span>
              <span className="text-xs font-bold tracking-wide text-gray-700">
                WHAT&apos;S NEXT?
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Approve this GRN to notify the site incharge and mark the
                purchase order ready to bill for accounts.
              </p>
              <Button
                className="w-auto py-2 px-6 shrink-0"
                onClick={openApproveConfirm}
                disabled={approving}
              >
                {approving ? "Approving..." : "Approve GRN"}
              </Button>
            </div>
          </div>

          {/* Mobile: compact text + pill button */}
          <div className="md:hidden px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600 flex items-center gap-1.5">
              <span className="text-base leading-none">✨</span>
              All items received?
            </p>
            <Button
              className="w-auto py-2 px-6 rounded-full shrink-0"
              onClick={openApproveConfirm}
              disabled={approving}
            >
              {approving ? "Approving..." : "Approve"}
            </Button>
          </div>
        </div>
      )}

      {/* PO Items + Received Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: PO Items */}
        <div className="border rounded-md p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-secondary">PO Items</div>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                {poItemsPagination.count}
              </span>
            </div>
            <button
              type="button"
              className="text-primary text-sm flex items-center gap-1 disabled:text-gray-400"
              onClick={handleRefreshPoItems}
              disabled={refreshing}
            >
              <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {poItemsLoading ? (
            <div className="flex justify-center py-6">
              <Loader size={3} />
            </div>
          ) : poItems.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-4">
              No PO items found
            </div>
          ) : (
            <div id="scrollablePoItemsDiv" className="max-h-[60vh] overflow-y-auto">
              <InfiniteScroll
                dataLength={poItems.length}
                next={loadMorePoItems}
                hasMore={poItemsPagination.offset < poItemsPagination.count}
                scrollableTarget="scrollablePoItemsDiv"
              >
                <div className="space-y-2">
                  {poItems.map((poItem) => (
                    <div
                      key={poItem.line_item_id}
                      className={`border rounded-md p-2 flex justify-between items-center gap-2 ${
                        poItem.is_deleted ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div
                            className={`text-sm font-medium truncate ${
                              poItem.is_deleted
                                ? "text-gray-400 line-through"
                                : "text-secondary"
                            }`}
                          >
                            {poItem.name}
                          </div>
                          {poItem.is_deleted && (
                            <span className="shrink-0 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-100 text-red-700">
                              Deleted
                            </span>
                          )}
                        </div>
                        {poItem.sku && (
                          <div className="text-xs text-gray-500 truncate">
                            SKU: {poItem.sku}
                          </div>
                        )}
                        <div className="text-xs mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className="text-gray-500">
                            Required:{" "}
                            <span className="font-medium text-gray-700">
                              {poItem.quantity} {poItem.unit}
                            </span>
                          </span>
                          <span className="text-gray-500">
                            Received:{" "}
                            <span className="font-medium text-gray-700">
                              {poItem.received_quantity} {poItem.unit}
                            </span>
                          </span>
                          <span
                            className={
                              Number(poItem.remaining_quantity) > 0
                                ? "text-gray-500"
                                : "text-green-600"
                            }
                          >
                            Remaining:{" "}
                            <span className="font-medium">
                              {poItem.remaining_quantity} {poItem.unit}
                            </span>
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-primary shrink-0 disabled:text-gray-300"
                        title={
                          poItem.is_deleted
                            ? "This PO item was removed upstream"
                            : "Add to received items"
                        }
                        onClick={() => openAddModal(poItem)}
                        disabled={poItem.is_deleted}
                      >
                        <FiPlus size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
              {poItemsPaginationLoading && (
                <div className="flex justify-center py-3">
                  <Loader size={3} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Received Items */}
        <div className="border rounded-md p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-secondary">
              Received Items
            </div>
            <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
              {itemsPagination.count}
            </span>
          </div>

          {itemsLoading ? (
            <div className="flex justify-center py-6">
              <Loader size={3} />
            </div>
          ) : receivedItems.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6 border border-dashed rounded-md">
              No items received yet. Use + on the left to add one.
            </div>
          ) : (
            <div id="scrollableGrnItemsDiv" className="max-h-[60vh] overflow-y-auto">
              <InfiniteScroll
                dataLength={receivedItems.length}
                next={loadMoreItems}
                hasMore={itemsPagination.offset < itemsPagination.count}
                scrollableTarget="scrollableGrnItemsDiv"
              >
                <div className="space-y-2">
                  {receivedItems.map((row) => (
                    <div
                      key={row.id}
                      className="flex items-center gap-3 border rounded-md p-2.5 bg-white hover:shadow-sm transition-shadow"
                    >
                      {row.photo ? (
                        <img
                          src={row.photo}
                          alt={row.item}
                          className="h-12 w-12 object-cover rounded border shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded border bg-gray-50 flex items-center justify-center text-gray-300 shrink-0">
                          <FiImage size={20} />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-secondary truncate">
                          {row.item}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                          <span>Received: {row.received_amount}</span>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              ITEM_STATUS_STYLES[row.item_status] ||
                              "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {ITEM_STATUS_LABELS[row.item_status] ||
                              formatLabel(row.item_status)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="text-red-500 hover:text-red-600 shrink-0"
                        title="Remove"
                        onClick={() => openDeleteConfirm(row)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
              {itemsPaginationLoading && (
                <div className="flex justify-center py-3">
                  <Loader size={3} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Received Item Modal */}
      <Modal
        isOpen={!!addModalItem}
        title={addModalItem ? addModalItem.name : ""}
        footer={
          <>
            <button
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              onClick={closeAddModal}
              disabled={adding}
            >
              Cancel
            </button>
            <Button
              className="w-auto py-2 px-6"
              onClick={handleSubmitAdd}
              disabled={adding}
            >
              {adding ? "Adding..." : "Add"}
            </Button>
          </>
        }
      >
        {addModalItem && (
          <div className="space-y-3 mx-auto max-w-xs">
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>
                Required: {addModalItem.quantity} {addModalItem.unit}
              </div>
              <div>
                Received: {addModalItem.received_quantity} {addModalItem.unit}
              </div>
              <div>
                Remaining: {addModalItem.remaining_quantity} {addModalItem.unit}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Received Quantity
              </label>
              <input
                type="number"
                min="0"
                max={addModalItem.remaining_quantity}
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={addForm.received_amount}
                onChange={(e) =>
                  handleAddFieldChange("received_amount", e.target.value)
                }
                disabled={adding}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Photo
              </label>
              <input
                type="file"
                accept="image/*"
                className="text-xs"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleAddPhotoChange(file);
                }}
                disabled={adding}
              />
              {addForm.photoPreview && (
                <img
                  src={addForm.photoPreview}
                  alt={addModalItem.name}
                  className="mt-2 h-20 w-20 object-cover rounded border"
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title="Remove Item"
        message={CONFIRM_DELETE_GRN_ITEM}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteConfirm}
        loading={deleting}
      />

      {/* Approve Confirmation Modal */}
      <Modal
        isOpen={approveConfirmOpen}
        title="Approve GRN"
        footer={
          <>
            <button
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              onClick={closeApproveConfirm}
              disabled={approving}
            >
              Cancel
            </button>
            <Button
              className="w-auto py-2 px-6"
              onClick={confirmApprove}
              disabled={approving || loadingMismatch}
            >
              {approving ? "Approving..." : "Yes, Approve"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 text-center">
            This marks the GRN as fully received, notifies the site incharge,
            and tells accounts the purchase order is ready to bill. Are you
            sure you want to approve?
          </p>

          {loadingMismatch ? (
            <div className="flex justify-center py-2">
              <Loader size={3} />
            </div>
          ) : (
            mismatchItems.length > 0 && (
              <div className="border border-yellow-200 bg-yellow-50 rounded-md p-3 text-sm">
                <div className="font-medium text-yellow-800 mb-1.5">
                  {mismatchItems.length} item(s) not fully received - approving
                  will still mark the PO ready to bill:
                </div>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {mismatchItems.map((item) => (
                    <li
                      key={item.line_item_id}
                      className="flex justify-between gap-2 text-yellow-900"
                    >
                      <span className="truncate">{item.name}</span>
                      <span className="shrink-0">
                        {item.received_quantity}/{item.quantity} {item.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GrnDetails;
